import { navigate } from '@reach/router';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select, { components, InputProps } from 'react-select';
import { selectComponentResources } from 'reducers/componentResources';
import {
    getCultureCodeFromLocalStorage,
    storeCultureCodeInLocalStorage,
} from '../../helpers/cache';
import { getPathname, setBackEndLanguage } from '../../helpers/language';
import { updateCultureCode } from '../../reducers/language';
import { selectSignedIn } from 'reducers/auth';
import GoogleTranslateManager from './GoogleTranslateManager';
import i18n from 'i18n';

type SelectOption = {
    value: string;
    label: string;
};

export type LanguageItem = {
    name: string;
    cultureInfo: string;
    imagePath: string;
    imageExtension: string;
    imageUrl: string;
};

const LanguageDropdown: React.FC = () => {
    const dispatch = useDispatch();
    const isSignedIn = useSelector(selectSignedIn);
    const googleTranslate = GoogleTranslateManager.getInstance();
    
    const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
    const [flagImageError, setFlagImageError] = useState<{[key: string]: boolean}>({});
    const [previousSignInStatus, setPreviousSignInStatus] = useState(isSignedIn);

    const toggleLanguageDropdown = () => {
        setLanguageDropdownOpen((prev) => !prev);
    };

    const closeLanguageDropdown = () => {
        setLanguageDropdownOpen(false);
    };

    const { languageList } = useSelector(selectComponentResources);
    const cultureCode = getCultureCodeFromLocalStorage();

    // Map culture codes to Google Translate language codes
    const mapCultureCodeToGoogleTranslate = (cultureCode: string): string => {
        const mapping: { [key: string]: string } = {
            'en-US': 'en',
            'es-ES': 'es'
        };
        return mapping[cultureCode] || 'en';
    };

    // Initialize Google Translate when signed in
    useEffect(() => {
        // Only act on sign-in status changes, not on every render
        if (isSignedIn && !previousSignInStatus) {
            // User just signed in
            
            // Check for saved language preference first
            const savedLanguage = localStorage.getItem('selectedLanguage');
            const googleLang = savedLanguage || mapCultureCodeToGoogleTranslate(cultureCode);
            
            
            // Save the preference if not already saved
            if (!savedLanguage) {
                localStorage.setItem('selectedLanguage', googleLang);
            }
            
            // Only initialize Google Translate if not English
            // For English, we don't need Google Translate at all
            if (googleLang !== 'en') {
                // Initialize with a delay to ensure DOM is ready
                setTimeout(() => {
                    googleTranslate.initialize(googleLang).then((success) => {
                        if (success) {
                            googleTranslate.hideUIElements();
                            
                            // Apply the saved language preference
                            setTimeout(() => {
                                googleTranslate.applyLanguage(googleLang);
                            }, 1000);
                        }
                    });
                }, 500);
            } else {
            }
        } else if (!isSignedIn && previousSignInStatus) {
            // User just signed out - cleanup safely to revert translations
            // Wrap in setTimeout to allow React to complete its updates first
            setTimeout(() => {
                googleTranslate.cleanup();
            }, 0);
        }
        
        // Update previous status
        setPreviousSignInStatus(isSignedIn);
    }, [isSignedIn, previousSignInStatus, cultureCode, googleTranslate]);

    // Also initialize on mount if already signed in
    useEffect(() => {
        if (isSignedIn) {
            
            // Check for saved language preference first
            const savedLanguage = localStorage.getItem('selectedLanguage');
            const googleLang = savedLanguage || mapCultureCodeToGoogleTranslate(cultureCode);
            
            
            // Save the preference if not already saved
            if (!savedLanguage) {
                localStorage.setItem('selectedLanguage', googleLang);
            }
            
            // Only initialize Google Translate if not English
            // For English, we don't need Google Translate at all
            if (googleLang !== 'en') {
                // Longer delay on mount to ensure everything is loaded
                setTimeout(() => {
                    googleTranslate.initialize(googleLang).then((success) => {
                        if (success) {
                            googleTranslate.hideUIElements();
                            
                            // Apply the language preference
                            setTimeout(() => {
                                googleTranslate.applyLanguage(googleLang);
                            }, 1500);
                        }
                    });
                }, 1500);
            } else {
            }
        }
        
        // Cleanup on unmount
        return () => {
            // Only cleanup if component is unmounting while signed in
            if (isSignedIn) {
                setTimeout(() => {
                    googleTranslate.cleanup();
                }, 0);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only on mount

    const getLanguageOptions = () => {
        // Return hardcoded language names to ensure consistency across all languages
        const options = [
            { value: 'en-US', label: 'English-US' },
            { value: 'es-ES', label: 'Español' }
        ];
        
        // Only return options that exist in the languageList
        return options.filter(option => 
            languageList?.some((item: LanguageItem) => item.cultureInfo === option.value)
        );
    };

    const languageOptions = getLanguageOptions();

    const findSelectedLanguage = () => {
        return languageOptions?.find((l: SelectOption) => l.value === cultureCode);
    };

    const getFlag = () => {
        const selectedLanguage =
            languageList && languageList.find((l: LanguageItem) => l.cultureInfo === cultureCode);
        
        if (selectedLanguage) {
            if (selectedLanguage.imageUrl && selectedLanguage.imageUrl.trim() !== '') {
                return selectedLanguage.imageUrl;
            }
            
            if (selectedLanguage.imagePath && selectedLanguage.imageExtension) {
                return `${selectedLanguage.imagePath}.${selectedLanguage.imageExtension}`;
            }
            
            const flagName = cultureCode?.toLowerCase() || 'en-us';
            return `/assets/flags/${flagName}.png`;
        }
        
        return null;
    };

    const handleFlagImageError = (cultureCode: string) => {
        setFlagImageError(prev => ({ ...prev, [cultureCode]: true }));
    };

    const shouldShowFlagImage = (flagUrl: string | null) => {
        return flagUrl && !flagImageError[cultureCode || ''];
    };

    const selectLanguage = async (e: any) => {
        const selectedCultureCode = e.value;
        const googleLangCode = mapCultureCodeToGoogleTranslate(selectedCultureCode);
        
        // Mark language change time
        localStorage.setItem('lastLanguageChangeTime', Date.now().toString());
        
        // Save language preference
        localStorage.setItem('selectedLanguage', googleLangCode);
        
        // Signal to allow translations
        if ((window as any).googleTranslateCoordinator) {
            (window as any).googleTranslateCoordinator.allowTranslation();
        }
        
        // Defer Google Translate operations to avoid React conflicts
        requestAnimationFrame(async () => {
            // Apply Google Translate if user is signed in
            if (isSignedIn) {
                if (googleTranslate.isReady()) {
                    googleTranslate.applyLanguage(googleLangCode);
                } else {
                    // Initialize if not ready
                    await googleTranslate.initialize(googleLangCode);
                    googleTranslate.hideUIElements();
                }
                
                // Force refresh translation after a delay
                setTimeout(() => {
                    if ((window as any).googleTranslateHelper) {
                        (window as any).googleTranslateHelper.forceRefreshTranslation();
                    }
                }, 1000);
            }
        });
        
        // Update backend language and local storage
        await setBackEndLanguage(selectedCultureCode);
        storeCultureCodeInLocalStorage(selectedCultureCode);
        dispatch(updateCultureCode(selectedCultureCode));
        i18n.changeLanguage(selectedCultureCode);
        
        // Navigate to the new language path if needed
        const pathname = getPathname(selectedCultureCode);
        if (pathname && pathname !== window.location.pathname) {
            navigate(pathname);
        }
        
        toggleLanguageDropdown();
    };

    const selectStyles = {
        control: (base: any) => ({
            ...base,
            backgroundColor: '#F9F9FB',
            '&:hover': {
                backgroundColor: '#E1E5E8',
            },
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#A5A5A5' : '#F9F9FB',
            color: '#000000',
        }),
    };

    const Dropdown = ({ target }: any) => (
        <div className="DropdownRegionToggleContainer">{target}</div>
    );

    const Control = ({ children, ...props }: any) => (
        <div className="CustomControl">
            <components.Control {...props} className="InputField">
                {children}
            </components.Control>
        </div>
    );

    const Input = ({ ...props }: InputProps) => (
        <div className="CustomRegionInput">
            <components.Input {...props} />
        </div>
    );

    const Menu = ({ ...props }: any) => (
        <div className="CustomMenuListContainer notranslate" translate="no">
            <div className="CustomMenuContainer">
                <components.Menu {...props} className="CustomMenu notranslate">
                    <div className="notranslate" translate="no">
                        {props.children}
                    </div>
                </components.Menu>
            </div>
        </div>
    );
    
    const Option = ({ ...props }: any) => (
        <div className="notranslate" translate="no">
            <components.Option {...props}>
                <span className="notranslate" translate="no">{props.data.label}</span>
            </components.Option>
        </div>
    );

    return (
        <div className={`LanguageDropdown ${languageDropdownOpen ? 'DropdownOpen' : ''}`} style={!isSignedIn ? { marginRight: 22 } : {}}>
            {languageDropdownOpen && (
                <div className="ClickOffCatcher" onClick={closeLanguageDropdown}></div>
            )}
            <Dropdown
                target={
                    <div className="RegionSelectValueContainer" onClick={toggleLanguageDropdown}>
                        <div
                            className={`Flag ${!shouldShowFlagImage(getFlag()) ? 'fallback' : ''}`}
                            style={
                                shouldShowFlagImage(getFlag())
                                    ? { backgroundImage: `url(${getFlag()})` }
                                    : {}
                            }
                        >
                            {shouldShowFlagImage(getFlag()) && (
                                <img
                                    src={getFlag() || ''}
                                    alt="Language flag"
                                    style={{ display: 'none' }}
                                    onError={() => handleFlagImageError(cultureCode || '')}
                                />
                            )}
                        </div>
                    </div>
                }
            />
            {languageDropdownOpen && languageOptions && (
                <>
                    <div className="Triangle" />
                    <Select
                        isSearchable={false}
                        autoFocus={true}
                        onBlur={toggleLanguageDropdown}
                        backspaceRemovesValue={false}
                        controlShouldRenderValue={false}
                        hideSelectedOptions={false}
                        isClearable={false}
                        menuIsOpen
                        maxMenuHeight={125}
                        onChange={selectLanguage}
                        options={languageOptions}
                        placeholder=""
                        tabSelectsValue={false}
                        styles={selectStyles}
                        value={findSelectedLanguage()}
                        className="notranslate"
                        components={{
                            Control: (inputProps) => <Control {...inputProps} />,
                            Input: (inputProps: InputProps) => <Input {...inputProps} />,
                            DropdownIndicator: null,
                            Menu: (inputProps) => <Menu {...inputProps} />,
                            Option: (inputProps) => <Option {...inputProps} />,
                        }}
                        classNamePrefix="LanguageDropdown"
                    />
                </>
            )}
        </div>
    );
};

export default LanguageDropdown;